import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Portfolio.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';

const PORTFOLIO_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}/portfolio#webpage`,
    "url": `${SITE_URL}/portfolio`,
    "name": "Our Work | Web & App Development Portfolio | Nimai Solutions",
    "description": "Explore Nimai Solutions' portfolio of web development and mobile app projects. See real results delivered to businesses across Vadodara, Gujarat and India.",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home",      "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "Portfolio",  "item": `${SITE_URL}/portfolio` }
        ]
    }
};

const Portfolio = () => {
    useSEO({
        title:       "Our Work | Web & App Development Portfolio | Nimai Solutions",
        description: "Explore Nimai Solutions' portfolio of web development, Flutter and mobile app projects. Real results delivered to businesses across Vadodara, Gujarat and India.",
        canonical:   `${SITE_URL}/portfolio`,
        keywords:    "web development portfolio Vadodara, app development case studies India, Nimai Solutions projects, Flutter app portfolio Gujarat",
        schema:      PORTFOLIO_SCHEMA,
    });

    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState('All');

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/projects');
                if (ok) {
                    setProjects(Array.isArray(result) ? result : result.data || []);
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProjects();
    }, []);

    const handleProjectClick = (id) => {
        navigate(`/portfolio/${id}`);
    };

    const getProjectCategory = (project) => project.service?.title || 'General';

    const filteredProjects = filter === 'All'
        ? projects
        : projects.filter(p => getProjectCategory(p) === filter);

    // Dynamic categories based on fetched projects
    const categories = ['All', ...new Set(projects.map(p => getProjectCategory(p)))];

    return (
        <div className="portfolio-container">
            <Navbar />

            <section className="portfolio-hero">
                <div className="hero-grid-bg"></div>
                <motion.div
                    className="portfolio-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="hero-subtitle">Our Work</span>
                    <h1>Engineering <span>Digital</span> Excellence.</h1>
                    <p>We transform complex challenges into seamless digital experiences. Explore our latest innovations across various industries.</p>
                </motion.div>
            </section>

            <section className="portfolio-content">
                <div className="portfolio-filters">
                    {categories.map((cat, i) => (
                        <motion.button
                            key={cat}
                            className={`filter-btn ${filter === cat ? 'active' : ''}`}
                            onClick={() => setFilter(cat)}
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                        >
                            {cat}
                        </motion.button>
                    ))}
                </div>

                {isLoading ? (
                    <div className="portfolio-loading" style={{ textAlign: 'center', padding: '100px 0' }}>
                        <div className="loader"></div>
                        <p style={{ marginTop: '20px', color: '#94a3b8' }}>Curating our best work...</p>
                    </div>
                ) : (
                    <motion.div
                        className="projects-grid"
                        layout
                    >
                        <AnimatePresence>
                            {filteredProjects.map((project) => (
                                <motion.div
                                    layout
                                    key={project.id}
                                    className="project-card"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.4 }}
                                    whileHover={{ y: -10 }}
                                    onClick={() => handleProjectClick(project.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="project-image-wrapper">
                                        <img src={project.image_url || project.image} alt={project.title} loading="lazy" />
                                        <div className="project-overlay">
                                            <div className="project-btn">
                                                View Project <FaExternalLinkAlt />
                                            </div>
                                        </div>
                                        <div className="project-category-badge" style={{ backgroundColor: project.theme_color }}>
                                            {getProjectCategory(project)}
                                        </div>
                                    </div>
                                    <div className="project-info">
                                        <div className="project-tags">
                                            {project.tags ? (
                                                project.tags.map(tag => <span key={tag}>{tag}</span>)
                                            ) : (
                                                <>
                                                    <span>{project.client}</span>
                                                    <span>{project.date}</span>
                                                </>
                                            )}
                                        </div>
                                        <h3>{project.title}</h3>
                                        <p>{project.description}</p>
                                        <div className="project-link-persistent">
                                            View Project <FaExternalLinkAlt />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}

                {!isLoading && filteredProjects.length === 0 && (
                    <div className="no-results">
                        <p>No projects found in this category yet.</p>
                    </div>
                )}
            </section>

            <section className="portfolio-cta">
                <div className="cta-content">
                    <h2>Have a <span>vision</span> in mind?</h2>
                    <p>Let's collaborate and build something extraordinary together.</p>
                    <button className="primary-btn" onClick={() => navigate('/contact')} style={{ backgroundColor: '#FF9F1C', border: 'none' }}>
                        Start a Project
                    </button>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Portfolio;
